import { FormFetchDataProps } from "@/components/_Shared/Form/Interfaces/FormFetchData";
import { GenericForm, GenericFormBGContainer } from "@/components/GenericForm";
import { Divider } from "@/components/misc";
import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { SubmittalFormData } from "./SubmittalForm";
import { SearchSelect } from "@/components/_Shared/Form/Inputs/SearchSelect";
import { TextArea } from "@/components/_Shared/Form/Inputs/TextArea";
import { Button } from "@/components/_Shared/Button";
import { Submit } from "@/components/_Shared/Form/Buttons/Submit";
import { Input } from "@/components/_Shared/Form/Inputs/Input";
import { FormChild } from "@/components/_Shared/Form/Containers/FormGroup";
import { FormGroup } from "@/components/_Shared/Form/Containers/FormGroup";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/_Shared/Form/Inputs/Checkbox";
import { openFolder } from "@/components/misc";
import { SubmittalProps } from "@/interfaces/submittal_types";

type SubmittalFormBaseProps = {
    submittal: SubmittalProps | null
    project_list: FormFetchDataProps[]
    employee_list: FormFetchDataProps[]
    category_list: FormFetchDataProps[]
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>
    method: "POST" | "PUT"
    isProcessing: boolean
    register: UseFormRegister<SubmittalFormData>
    errors: FieldErrors<SubmittalFormData>
    control: Control<SubmittalFormData>
}

export function SubmittalFormBase({ submittal, onSubmit, project_list, employee_list, category_list, isProcessing, method, register, errors, control }: SubmittalFormBaseProps) {
    const navigate = useNavigate()

    const status_options = ["Active", "Closed"].map((status) => ({value: status, label: status}))
    const folder_location = submittal?.folder_location

    const method_string = {
        POST: "Create Submittal",
        PUT: "Update Submittal"
    }

    return (
        <GenericForm form_id="submittal_creation" onSubmit={onSubmit}>
            <GenericFormBGContainer>
                <h2 className="text-2xl font-bold">{method === "POST" ? "Create" : "Update"} Submittal</h2>

                <Divider />

                {method === "PUT" && (
                    <>
                        <Input label="Submittal ID" type="text" name="submittal_id" register={register} readOnly={true} />
                        <div className="text-sm text-blue-500 underline cursor-pointer text-left" onClick={() => openFolder(folder_location ?? '')}>View Submittal</div>
                    </>
                )}
                
                <SearchSelect label="Project Name" options={project_list} name="project" control={control} readonly={method === "PUT"} />
                {errors.project && <p className="text-red-500">{errors.project.message}</p>}

                <FormGroup>
                    <FormChild>
                        <SearchSelect label="Assigned By" options={employee_list} name="assigned_by" control={control} />
                        {errors.assigned_by && <p className="text-red-500">{errors.assigned_by.message}</p>}
                    </FormChild>

                    <FormChild>
                        <SearchSelect label="Assigned To" options={employee_list} name="assigned_to" control={control} />
                        {errors.assigned_to && <p className="text-red-500">{errors.assigned_to.message}</p>}
                    </FormChild>

                </FormGroup>

                <FormGroup>
                    <FormChild>
                        <SearchSelect label="Status" options={status_options} name="status" control={control} />
                        {errors.status && <p className="text-red-500">{errors.status.message}</p>}
                    </FormChild>

                    <FormChild>
                        <SearchSelect label="Category" options={category_list} name="category" control={control} />
                        {errors.category && <p className="text-red-500">{errors.category.message}</p>}
                    </FormChild>
                </FormGroup>
                
                <FormGroup>
                    <FormChild>
                        <Input label="Received Date" type="date" name="received_date" register={register} />
                        {errors.received_date && <p className="text-red-500">{errors.received_date.message}</p>}
                    </FormChild>

                    <FormChild>
                        <Input label="Returned Date" type="date" name="completed_at" register={register} />
                        {errors.completed_at && <p className="text-red-500">{errors.completed_at.message}</p>}
                    </FormChild>
                </FormGroup>

                <TextArea label="Submittal Description" register={register} placeholder="Enter submittal description" name="sub_description" />
                {errors.sub_description && <p className="text-red-500">{errors.sub_description.message}</p>}

                {method === "PUT" && 
                    <>
                        <Input label="Submittal Folder Location" name="folder_location" register={register} type="text" tooltip="The folder location of the submittal. You should only change this if you are moving the submittal to a different folder or if the submittal location is not correct. The folder MUST be located within it's category folder." /> 
                        {errors.folder_location && <p className="text-red-500">{errors.folder_location.message}</p>}
                    </>
                }
                
                {method === "PUT" && <Checkbox label="Pinged" name="alert_status" register={register} />}

                <FormGroup> 
                    <Button className="" onClick={() => navigate("/submittals")}>Cancel</Button>
                    <Submit isSubmitting={isProcessing} label={method_string[method]} />
                </FormGroup>
            </GenericFormBGContainer>
        </GenericForm>
    );
}
